<template>
  <div class="user-management">
    <!-- 搜索和操作栏 -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="8">
        <el-input
          v-model="searchQuery"
          placeholder="请输入用户名、邮箱、昵称、OpenID或手机号"
          clearable
          @keyup.enter="handleSearch"
        >
          <template #append>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </template>
        </el-input>
      </el-col>
      <el-col :span="4">
        <el-button type="success" @click="showEditDialog = true; resetForm()">新增用户</el-button>
      </el-col>
    </el-row>

    <!-- 用户列表 -->
    <el-table
      :data="users"
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa' }"
    >
      <el-table-column prop="id" label="ID" width="60" />
      <el-table-column prop="username" label="用户名" min-width="100" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.username || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="昵称/头像" min-width="120" show-overflow-tooltip>
        <template #default="scope">
          <div v-if="scope.row.user_type === 'wx_user'">
            <div style="display: flex; align-items: center; gap: 6px;">
              <el-avatar v-if="scope.row.avatar_url" :src="scope.row.avatar_url" :size="24" />
              <span>{{ scope.row.nickname || '微信用户' }}</span>
            </div>
          </div>
          <div v-else style="color: #909399;">-</div>
        </template>
      </el-table-column>
      <el-table-column prop="email" label="邮箱" min-width="150" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.email || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="openid" label="OpenID" min-width="160" show-overflow-tooltip>
        <template #default="scope">
          <div v-if="scope.row.openid" style="font-size: 12px; word-break: break-all;">
            {{ scope.row.openid }}
          </div>
          <div v-else style="color: #909399;">-</div>
        </template>
      </el-table-column>
      <el-table-column prop="mobile" label="手机号" width="110">
        <template #default="scope">
          {{ scope.row.mobile || '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="role" label="角色" width="80">
        <template #default="scope">
          <el-tag :type="scope.row.role === 'admin' ? 'danger' : 'info'">
            {{ scope.row.role === 'admin' ? '管理员' : '普通用户' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="is_active" label="状态" width="70">
        <template #default="scope">
          <el-tag :type="scope.row.is_active ? 'success' : 'danger'">
            {{ scope.row.is_active ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="140" />
      <el-table-column prop="updated_at" label="更新时间" width="140">
        <template #default="scope">
          {{ scope.row.updated_at || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="160" fixed="right">
        <template #default="scope">
          <el-button 
            v-if="scope.row.user_type === 'normal_user'"
            size="small"
            type="primary"
            @click="onEdit(scope.row)"
          >
            编辑
          </el-button>
          <el-button
            v-if="scope.row.user_type === 'normal_user'"
            size="small"
            type="primary"
            @click="onResetPassword(scope.row)"
          >
            重置密码
          </el-button>
          <el-button
            v-if="scope.row.is_active"
            size="small"
            type="warning"
            @click="onToggleStatus(scope.row, false)"
          >
            禁用
          </el-button>
          <el-button
            v-else
            size="small"
            type="success"
            @click="onToggleStatus(scope.row, true)"
          >
            启用
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      style="margin-top: 20px; text-align: right;"
      background
      layout="prev, pager, next, jumper, ->, total"
      :total="total"
      :page-size="pageSize"
      v-model:current-page="currentPage"
      @current-change="handlePageChange"
    />

    <!-- 新增/编辑用户弹窗 -->
    <el-dialog
      :title="editMode ? '编辑用户' : '新增用户'"
      v-model="showEditDialog"
      width="500px"
    >
      <el-form
        ref="userForm"
        :model="editForm"
        :rules="userRules"
        label-width="80px"
      >
        <el-form-item label="用户名" prop="username">
          <el-input v-model="editForm.username" :disabled="editMode" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" type="email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="密码" prop="password" v-if="!editMode">
          <el-input
            v-model="editForm.password"
            type="password"
            show-password
            placeholder="默认密码：123456（建议用户首次登录后修改）"
          />
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">
            💡 提示：如果不填写密码，系统将使用默认密码 "123456"
          </div>
        </el-form-item>
        <el-form-item label="角色" prop="role">
          <el-select v-model="editForm.role" placeholder="请选择角色">
            <el-option label="普通用户" value="user" />
            <el-option label="管理员" value="admin" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch v-model="editForm.is_active" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showEditDialog = false">取消</el-button>
          <el-button type="primary" @click="handleSave" :loading="saving">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import {
  getUsers,
  addUser,
  updateUser,
  enableUser,
  disableUser,
  resetUserPassword
} from '../api/auth';

export default {
  name: 'UserManagement',
  setup() {
    const users = ref([]);
    const loading = ref(false);
    const saving = ref(false);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const showEditDialog = ref(false);
    const editMode = ref(false);

    const editForm = reactive({
      id: null,
      username: '',
      email: '',
      password: '',
      role: 'user',
      is_active: true
    });

    const userRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
      ],
      email: [
        { required: true, message: '请输入邮箱', trigger: 'blur' },
        { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
      ],
      password: [
        { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
      ],
      role: [
        { required: true, message: '请选择角色', trigger: 'change' }
      ]
    };

    const userForm = ref();

    const loadUsers = async () => {
      try {
        loading.value = true;
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        };
        if (searchQuery.value) {
          params.query = searchQuery.value;
        }
        
        const response = await getUsers(params);
        users.value = response.data || [];
        total.value = response.total || 0;
      } catch (error) {
        console.error('加载用户列表失败:', error);
        ElMessage.error('加载用户列表失败');
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      loadUsers();
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      loadUsers();
    };

    const onEdit = (user) => {
      editMode.value = true;
      Object.assign(editForm, {
        id: user.id,
        username: user.username,
        email: user.email || '',
        password: '',
        role: user.role || 'user',
        is_active: Boolean(user.is_active)  // 确保转换为布尔值
      });
      showEditDialog.value = true;
    };

    const onToggleStatus = async (user, enable) => {
      try {
        const action = enable ? '启用' : '禁用';
        const userName = user.user_type === 'wx_user' 
          ? (user.nickname || '微信用户') 
          : user.username;
        await ElMessageBox.confirm(
          `确定要${action}用户 "${userName}" 吗？`,
          '确认操作',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );

        if (enable) {
          await enableUser(user.id);
        } else {
          await disableUser(user.id);
        }

        ElMessage.success(`${action}成功`);
        loadUsers();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error);
          ElMessage.error('操作失败');
        }
      }
    };

    const onResetPassword = async (user) => {
      try {
        const userName = user.username || '用户';
        await ElMessageBox.confirm(
          `确定要重置用户 "${userName}" 的密码吗？\n重置后的密码将为：123456`,
          '确认重置密码',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );

        await resetUserPassword(user.id, '123456');
        ElMessage.success(`用户 "${userName}" 的密码已重置为：123456`);
        loadUsers();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('重置密码失败:', error);
          ElMessage.error('重置密码失败');
        }
      }
    };

    const handleSave = async () => {
      try {
        await userForm.value.validate();
        saving.value = true;

        if (editMode.value) {
          // eslint-disable-next-line no-unused-vars
          const { password, ...updateData } = editForm;
          await updateUser(editForm.id, updateData);
          ElMessage.success('更新成功');
        } else {
          await addUser(editForm);
          ElMessage.success('添加成功');
        }

        showEditDialog.value = false;
        loadUsers();
      } catch (error) {
        console.error('保存失败:', error);
        ElMessage.error('保存失败');
      } finally {
        saving.value = false;
      }
    };

    const resetForm = () => {
      editMode.value = false;
      Object.assign(editForm, {
        id: null,
        username: '',
        email: '',
        password: '123456', // 设置默认密码
        role: 'user',
        is_active: true
      });
      if (userForm.value) {
        userForm.value.resetFields();
      }
    };

    onMounted(() => {
      loadUsers();
    });

    return {
      users,
      loading,
      saving,
      searchQuery,
      currentPage,
      pageSize,
      total,
      showEditDialog,
      editMode,
      editForm,
      userRules,
      userForm,
      handleSearch,
      handlePageChange,
      onEdit,
      onToggleStatus,
      onResetPassword,
      handleSave,
      resetForm
    };
  }
};
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style> 